import { useEventEmitter } from 'ahooks'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ChildCommentsFragment,
  PaginationInput,
  RequestedCommentFragment,
  RequestedPostFragment,
  useGetPostChildCommentsLazyQuery,
} from '@/generated/graphql'

import { PostCommentCreate } from './post-comment-create.component'
import { PostCommentItem } from './post-comment-item.component'
import s from './post-comments.module.less'

export type SortType = 'likedCount' | 'createdAt'
interface PostCommentProps {
  post: RequestedPostFragment
  comment: RequestedCommentFragment & ChildCommentsFragment
  sort: SortType
  disableCreate?: boolean
  childCommentPagination?: PaginationInput
}

export const PostComment: React.FC<PostCommentProps> = ({
  post,
  comment,
  sort,
  childCommentPagination,
  disableCreate = false,
}) => {
  const [t] = useTranslation()

  const onChangeContent$ = useEventEmitter<string | null>()

  const [isReplying, setIsReplying] = React.useState(false)

  const { edges, pageInfo, totalCount } = comment.comments

  const [loadComments] = useGetPostChildCommentsLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      postId: comment.postId,
      commentId: comment.commentId,
      childCommentPagination: childCommentPagination || {
        last: totalCount - edges.length,
        before: pageInfo.startCursor,
      },
    },
  })

  React.useEffect(() => {
    if (totalCount - edges.length > 0 && childCommentPagination) {
      loadComments()
    }
  }, [sort, comment])

  const onClickReply = (mention: string) => {
    setIsReplying(true)
    window.setTimeout(() => {
      onChangeContent$.emit(mention)
    }, 0)
  }

  const commentComponents = comment.comments.edges.map((e) => (
    <PostCommentItem
      key={e.node.commentId}
      isChildComment
      post={post}
      comment={e.node}
      onClickReply={onClickReply}
      disableCreate={disableCreate}
    />
  ))

  const moreComments = totalCount - edges.length > 0 && (
    <div className={cls(s.moreComments, s.actionButton)} onClick={() => loadComments()}>
      {t('comment.other.reply', { count: totalCount - edges.length })}
    </div>
  )

  const childComments = totalCount > 0 && (
    <div className={s.childCommentsContainer}>
      {moreComments}
      <div className={s.childComments}>{commentComponents}</div>
    </div>
  )

  return (
    <PostCommentItem post={post} comment={comment} onClickReply={onClickReply} disableCreate={disableCreate}>
      {childComments}
      {isReplying && (
        <PostCommentCreate
          post={post}
          parentComment={comment}
          placeholder={t('post.replyComment')}
          autoFocus
          onChangeContent$={onChangeContent$}
        />
      )}
    </PostCommentItem>
  )
}
